import { usePortalMessage } from "../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * `Add credit`, and `Add a payment method` — **neither has a card field of ours**.
 *
 * THE DESIGN ASKS FOR SOMETHING THAT MUST NOT BE BUILT. `design/WeTalk.dc.html`'s
 * `bill.cardOpen` dialog draws Card number / Expiry / CVC / Postcode / Name on card. Building
 * it would put a primary account number inside WeTalk's own DOM, which is exactly what the
 * EveryPay integration exists to prevent: card data never touches WeTalk (hosted Payform,
 * SAQ A, last four digits only). So both dialogs mount **the processor's own form**, from
 * the processor's own script, and the platform learns a single-use token from it. Adding a card
 * is buying credit (RB-23; see `AddCardDialog`).
 *
 * `Add credit` buys a credit pack with a card entered in that same form (amended 2026-09-23,
 * plan-002 RB-31). It used to send the first saved card's `paymentMethodId` as `card_token` —
 * a WeTalk row id where `createTopUp` takes the processor's single-use `ctn_` token, so every
 * real top-up would have been charged against a string EveryPay has never seen. RB-23's contract
 * leaves no other way: the browser can never name a stored card (the server refuses `cus_`/`crd_`
 * tokens by name, and no route charges a saved card by its id on demand — a saved card is
 * charged only by automatic top-up, server to server). So the dialog opens the Payform with the
 * server's own amount for the chosen pack (`startHostedCardForm`), and charges the token the
 * Payform returns through `createTopUp` for exactly that amount, without saving the card again.
 * The typed "any amount" field is gone for the same reason: the form is opened with the SERVER's
 * figure for a pack, never with one this page computed.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Dialog, Field, Segmented } from "@wetalk/ui";
import { CURRENCY_CODE, money } from "@wetalk/domain";
import { useEffect, useState, type ReactNode } from "react";

import { useGateway } from "../../api/provider.js";
import { QUERY_KEY } from "../../api/query.js";
import { formatMoney } from "../../money.js";
import type { HostedCardFormView, TopUpOutcomeView } from "../../api/gateway.js";
import { openPayform, type CardPurchaseOutcomeView, type CardPurchaseRequestView } from "../../api/slice/billing-card.js";
import { usePurchaseTermsGate } from "./purchase-terms-gate.js";
import "./billing.css";

/** The four amounts the design offers, as whole euro in minor units. */
const PRESET_MINOR = [10_000, 25_000, 50_000, 100_000] as const;

/** The server's name for a pack: `credit_<whole euro>` (RB-23's credit-pack list). */
function creditPackCodeOf(amountMinor: number): string {
  return `credit_${String(amountMinor / 100)}`;
}

export interface AddCreditDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

/** The element the processor's own script mounts the top-up form into. */
const TOP_UP_PAYFORM_ELEMENT_ID = "top-up-pay-form";

export function AddCreditDialog({ open, onClose }: AddCreditDialogProps): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const [chosenMinor, setChosenMinor] = useState<number>(50_000);
  const [form, setForm] = useState<HostedCardFormView | null>(null);
  const [outcome, setOutcome] = useState<TopUpOutcomeView | null>(null);
  const [message, setMessage] = usePortalMessage();

  const close = (): void => {
    setForm(null);
    setOutcome(null);
    setMessage(null);
    onClose();
  };

  // plan-014 review C-3 (PD-8): the purchase terms, accepted before the processor's form opens — never after a card.
  const termsGate = usePurchaseTermsGate(open);
  const start = useMutation({
    mutationFn: async () => {
      await termsGate.acceptIfNeeded();
      return gateway.startHostedCardForm(creditPackCodeOf(chosenMinor));
    },
    onSuccess: (opened) => {
      setForm(opened);
    },
    onError: (error: unknown) => {
      setMessage(() => (error instanceof Error ? error.message : portalText("finance", "m0102")));
    },
  });

  const pay = useMutation({
    // The amount is the one the SERVER opened the form with, and the token is the Payform's own
    // single-use `ctn_` token — never a saved card's row id, never a stored processor token.
    mutationFn: (request: { readonly opened: HostedCardFormView; readonly cardToken: string }) =>
      // FIX-MONEY C1: the NET credit, and the quote the form was opened for with the total (net + VAT) it opened with
      // — the card is charged exactly that total, never a figure the cardholder did not authenticate.
      gateway.buyCredit({
        amount: request.opened.net,
        billingQuoteId: request.opened.billingQuoteId,
        amountDue: request.opened.amount,
        cardToken: request.cardToken,
        saveCard: false,
      }),
    onSuccess: async (result) => {
      setOutcome(result);
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.creditBalance });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.ledger });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.chargeAttempt });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.billing });
    },
    onError: () => {
      setMessage(() => (portalText("finance", "m0103")));
    },
  });

  // TanStack Query keeps `mutate` stable across renders, so the effect below re-runs only for a
  // new form — the one thing that should mount the processor's script again.
  const { mutate: payWith } = pay;

  useEffect(() => {
    if (form === null) return;
    let cancelled = false;
    openPayform(form, TOP_UP_PAYFORM_ELEMENT_ID).then(
      (cardToken) => {
        if (!cancelled) payWith({ opened: form, cardToken });
      },
      (error: unknown) => {
        if (!cancelled) {
          setMessage(() => (error instanceof Error ? error.message : portalText("finance", "m0104")));
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [form, payWith, setMessage]);

  return (
    <Dialog
      open={open}
      title={portalText("finance", "m0105")}
      onClose={close}
      actions={
        <>
          <Button variant="secondary" onClick={close}>
            {outcome === null ? portalText("finance", "m0106") : portalText("finance", "m0107")}
          </Button>
          {form === null && outcome === null ? (
            <Button
              variant="primary"
              disabled={start.isPending || termsGate.blocking}
              onClick={() => {
                setMessage(null);
                start.mutate();
              }}
            >{portalText("finance", "m0108")}</Button>
          ) : null}
        </>
      }
    >
      {outcome !== null ? (
        <TopUpResult outcome={outcome} />
      ) : form === null ? (
        <>
          <p className="c-note">{portalText("finance", "m0109")}</p>
          <Field label={portalText("finance", "m0110")}>
            {() => (
              <Segmented
                name="topUpAmount"
                legend={portalText("finance", "m0110")}
                value={String(chosenMinor)}
                options={PRESET_MINOR.map((amount) => ({
                  value: String(amount),
                  label: formatMoney(money(amount, CURRENCY_CODE)),
                }))}
                onChange={(next) => {
                  setChosenMinor(Number(next));
                }}
              />
            )}
          </Field>
          <p className="c-note">{portalText("finance", "m0111")}</p>
          {termsGate.line}
        </>
      ) : (
        <>
          <FormTotal form={form} />
          <div id={TOP_UP_PAYFORM_ELEMENT_ID} />
          {pay.isPending ? <p className="c-note">{portalText("finance", "m0112")}</p> : null}
        </>
      )}
      {message === null ? null : <p className="c-note c-note-critical">{message}</p>}
    </Dialog>
  );
}

/** "50" for 50‰ → 5%, "22.5" for 225‰. */
function percentText(permille: number): string {
  return permille % 10 === 0 ? String(permille / 10) : (permille / 10).toFixed(1);
}

/**
 * FIX-MONEY C1 (2026-10-06): prices exclude VAT (OD-8), so before the card form the person sees the credit, the VAT and
 * the total — and the total is what the processor's form opens with, authenticates and charges.
 */
function FormTotal({ form }: { readonly form: HostedCardFormView }): ReactNode {
  usePortalTranslation("finance");
  return (
    <p className="c-note">{portalText("finance", "m0113")}<strong>{formatMoney(form.net)}</strong>{portalText("finance", "m0114")}{percentText(form.taxRatePermille)}%{" "}
      <strong>{formatMoney(form.tax)}</strong>{portalText("finance", "m0115")}<strong>{formatMoney(form.amount)}</strong>{portalText("finance", "m0116")}</p>
  );
}

/**
 * What came back.
 *
 * Four outcomes, and they are kept apart because they need four different things from the
 * customer: nothing, a different card, a bank authentication, or patience. The last one is
 * the important one — an ambiguous result means "go and look up what happened", never "try
 * again", because `merchant_ref` gives duplicate rejection rather than idempotency.
 */
function TopUpResult({ outcome }: { readonly outcome: TopUpOutcomeView }): ReactNode {
  usePortalTranslation("finance");
  if (outcome.status === "succeeded") {
    return (
      <p className="c-note">{portalText("finance", "m0117")}{formatMoney(outcome.amount)}{portalText("finance", "m0118")}{" "}
        {outcome.balanceAfter === null
          ? portalText("finance", "m0119")
          : portalText("finance", "m0120", { value1: formatMoney(outcome.balanceAfter) })}
      </p>
    );
  }
  if (outcome.status === "declined") {
    return (
      <p className="c-note c-note-critical">{portalText("finance", "m0121")}{outcome.declineMessage === null ? "" : `: ${outcome.declineMessage}`}{portalText("finance", "m0122")}</p>
    );
  }
  if (outcome.status === "requires_action" && outcome.redirectUrl !== null) {
    return (
      <p className="c-note">{portalText("finance", "m0123")}{" "}
        <a href={outcome.redirectUrl}>{portalText("finance", "m0124")}</a>{portalText("finance", "m0125")}</p>
    );
  }
  if (outcome.status === "requires_reauth") {
    return (
      <p className="c-note c-note-critical">{portalText("finance", "m0126")}</p>
    );
  }
  if (outcome.pendingReconciliation) {
    return (
      <p className="c-note">{portalText("finance", "m0127")}</p>
    );
  }
  return <p className="c-note">{portalText("finance", "m0128")}</p>;
}

export interface AddCardDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

/** The element the processor's own script mounts its form into. */
const PAYFORM_ELEMENT_ID = "pay-form";

/**
 * Adding a card is buying credit (plan-002 RB-23, product decision 2026-09-23).
 *
 * The processor documents no way to keep a card for automatic top-ups without a real payment, so
 * the card, the authorisation and the credit all come from one purchase. The customer picks a
 * pack; the API answers with what EveryPay's form opens with; EveryPay's own script mounts the
 * form below (the card is typed into the processor's frame, never into this page); the token it
 * returns is charged by the API for the pack's amount, and the card is kept from the
 * processor's answer.
 */
export function AddCardDialog({ open, onClose }: AddCardDialogProps): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const [chosenMinor, setChosenMinor] = useState<number>(50_000);
  const [form, setForm] = useState<HostedCardFormView | null>(null);
  const [outcome, setOutcome] = useState<CardPurchaseOutcomeView | null>(null);
  const [message, setMessage] = usePortalMessage();

  const close = (): void => {
    setForm(null);
    setOutcome(null);
    setMessage(null);
    onClose();
  };

  const purchase = useMutation({
    mutationFn: (request: CardPurchaseRequestView) => gateway.completeCardPurchase(request),
    onSuccess: async (result) => {
      setOutcome(result);
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.creditBalance });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.ledger });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.chargeAttempt });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.billing });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.paymentMethod });
    },
    onError: (error: unknown) => {
      setMessage(() => (error instanceof Error ? error.message : portalText("finance", "m0129")));
    },
  });

  // plan-014 review C-3 (PD-8): the purchase terms, accepted before the processor's form opens — never after a card.
  const termsGate = usePurchaseTermsGate(open);
  const start = useMutation({
    mutationFn: async () => {
      await termsGate.acceptIfNeeded();
      return gateway.startHostedCardForm(creditPackCodeOf(chosenMinor));
    },
    onSuccess: (opened) => {
      setForm(opened);
    },
    onError: (error: unknown) => {
      setMessage(() => (error instanceof Error ? error.message : portalText("finance", "m0102")));
    },
  });

  // TanStack Query keeps `mutate` stable across renders, so the effect below re-runs only for a
  // new form — the one thing that should mount the processor's script again.
  const { mutate: purchaseWith } = purchase;

  // Once the form's values are here and its element is on the page, hand both to the
  // processor's script and wait for the token it returns.
  useEffect(() => {
    if (form === null) return;
    let cancelled = false;
    openPayform(form, PAYFORM_ELEMENT_ID).then(
      (cardToken) => {
        if (!cancelled) purchaseWith({ creditPackCode: form.creditPackCode, cardToken, billingQuoteId: form.billingQuoteId, amountDue: form.amount });
      },
      (error: unknown) => {
        if (!cancelled) {
          setMessage(() => (error instanceof Error ? error.message : portalText("finance", "m0104")));
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [form, purchaseWith, setMessage]);

  return (
    <Dialog
      open={open}
      title={portalText("finance", "m0130")}
      onClose={close}
      actions={
        <>
          <Button variant="secondary" onClick={close}>
            {outcome === null ? portalText("finance", "m0106") : portalText("finance", "m0107")}
          </Button>
          {form === null && outcome === null ? (
            <Button
              variant="primary"
              disabled={start.isPending || termsGate.blocking}
              onClick={() => {
                setMessage(null);
                start.mutate();
              }}
            >{portalText("finance", "m0108")}</Button>
          ) : null}
        </>
      }
    >
      {outcome !== null ? (
        <>
          <TopUpResult outcome={outcome} />
          {outcome.paymentMethod !== null ? (
            <p className="c-note">
              {outcome.paymentMethod.brand} ···· {outcome.paymentMethod.lastFour}{portalText("finance", "m0131")}{outcome.paymentMethod.hasMandate ? portalText("finance", "m0132") : ""}.
            </p>
          ) : null}
          {outcome.cardNotSavedReason === null ? null : (
            <p className="c-note c-note-critical">{portalText("finance", "m0133")}{outcome.cardNotSavedReason}
            </p>
          )}
        </>
      ) : form === null ? (
        <>
          <p className="c-note">{portalText("finance", "m0134")}</p>
          <Field label={portalText("finance", "m0135")}>
            {() => (
              <Segmented
                name="cardPurchaseAmount"
                legend={portalText("finance", "m0135")}
                value={String(chosenMinor)}
                options={PRESET_MINOR.map((amount) => ({
                  value: String(amount),
                  label: formatMoney(money(amount, CURRENCY_CODE)),
                }))}
                onChange={(next) => {
                  setChosenMinor(Number(next));
                }}
              />
            )}
          </Field>
          <p className="c-note">{portalText("finance", "m0136")}</p>
          {termsGate.line}
        </>
      ) : (
        <>
          <FormTotal form={form} />
          <div id={PAYFORM_ELEMENT_ID} />
          {purchase.isPending ? <p className="c-note">{portalText("finance", "m0112")}</p> : null}
        </>
      )}
      {message === null ? null : <p className="c-note c-note-critical">{message}</p>}
    </Dialog>
  );
}
